import React, { createContext, useCallback, useContext, useState } from 'react';
import { noop } from '../util/functions';
import { randomId } from '../util/strings';

export enum MessageLevel {
    Success = 'success',
    Warning = 'warning',
    Error = 'error',
    Info = 'info',
}

export type FlashMessage = {
    id: string;
    message: NonNullable<React.ReactNode>;
    variant: MessageLevel;
    onClose?: () => void;
}

interface IFlashMessageContext {
    messages: FlashMessage[];
    addMessage: (message: Omit<FlashMessage, 'id'> & Partial<Pick<FlashMessage, 'id'>>) => string;
    removeMessage: (messageId: string) => void;
    clear: () => void;
}

const FlashMessageContext = createContext<IFlashMessageContext>({
    messages: [],
    addMessage: () => '',
    removeMessage: noop,
    clear: noop,
});

interface IFlashMessageProviderProps {
    children?: React.ReactNode;
}

export const FlashMessageProvider: React.FunctionComponent<IFlashMessageProviderProps> = (props) => {
    const [ messages, setMessages ] = useState<FlashMessage[]>([]);
    const addMessage = useCallback<IFlashMessageContext['addMessage']>((message) => {
        const messageId = message.id ?? randomId();
        setMessages(msgs => [ ...msgs, { ...message, id: messageId } ]);
        return messageId;
    }, []);
    const removeMessage = useCallback<IFlashMessageContext['removeMessage']>((messageId) => {
        setMessages(msgs => msgs.filter(msg => msg.id !== messageId));
    }, []);
    const clear = useCallback<IFlashMessageContext['clear']>(() => {
        setMessages([]);
    }, []);
    return <FlashMessageContext.Provider value={{ messages, addMessage, removeMessage, clear }} {...props}/>;
};

export const useFlashMessages = () => {
    const context = useContext(FlashMessageContext);
    return {
        ...context,
        getMessages: () => context.messages,
    };
};
